import type { LabelProps } from 'recharts';

import { measureTextWidth } from './utils/textWidth';

interface BarValueLabelOptions {
  formatter: (value: number) => string;

  /** Reads bottom to top inside the bar, or above it when the bar is too short. */
  vertical: boolean;

  fontFamily?: string;

  fontSize: number;

  /** Text colour inside the bar. */
  insideFill: string;

  /** Text colour above the bar. */
  outsideFill: string;
}

/** Padding between a value label and the bar edge, in pixels. */
const LABEL_PADDING = 4;

/** LabelList content drawing a bar's value, skipping empty values and bars too narrow for text. */
export function createBarValueLabel({
  formatter,
  vertical,
  fontFamily,
  fontSize,
  insideFill,
  outsideFill,
}: BarValueLabelOptions) {
  return function BarValueLabel(props: LabelProps) {
    const value = Number(props.value);
    const [x, y, width, height] = [
      Number(props.x),
      Number(props.y),
      Number(props.width),
      Number(props.height),
    ];
    if (
      props.value == null ||
      !Number.isFinite(value) ||
      !Number.isFinite(width) ||
      width < fontSize
    ) {
      return null;
    }

    const text = formatter(value);
    const centreX = x + width / 2;
    const textProps = {
      fontFamily,
      fontSize,
      dominantBaseline: 'central' as const,
    };

    if (!vertical) {
      return (
        <text
          {...textProps}
          x={centreX}
          y={y - LABEL_PADDING - fontSize / 2}
          textAnchor='middle'
          fill={outsideFill}
        >
          {text}
        </text>
      );
    }

    const fitsInside =
      measureTextWidth(text, fontSize, fontFamily) + 2 * LABEL_PADDING <=
      Math.abs(height);
    const anchorY = fitsInside ? y + height - LABEL_PADDING : y - LABEL_PADDING;

    return (
      <text
        {...textProps}
        x={centreX}
        y={anchorY}
        transform={`rotate(-90, ${centreX}, ${anchorY})`}
        textAnchor='start'
        fill={fitsInside ? insideFill : outsideFill}
      >
        {text}
      </text>
    );
  };
}
